import {FC, RefObject, useEffect, useRef} from 'react';
import {Box, Button, Flex, Link, Stack} from '@chakra-ui/react';
import {api, ESummaryStatus, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Input, Select, Svg, Textarea, useIsOpen, useScreenSize, ConfirmDialog} from 'ui-kit';

interface IProps {
  id: string;
  containerRef: RefObject<HTMLDivElement | null>;
}

interface IPromptForm {
  prompt: string;
  model_name: string;
  wiring_prefix: string;
  wiring_suffix: string;
  update_every_n_turns: number;
  char_limit: number;
  max_attempts: number;
}

const DEFAULTS: IPromptForm = {
  prompt: '',
  model_name: 'glm-5.1-chat',
  wiring_prefix: '[USER MEMORY (compact summary)]',
  wiring_suffix: '[END USER MEMORY]',
  update_every_n_turns: 20,
  char_limit: 4000,
  max_attempts: 2
};

const SummaryPromptTabFC: FC<IProps> = ({id, containerRef}) => {
  const {data: settings, isLoading} = api.summary.useSummaryPromptSettingsQuery(id);
  const {data: state} = api.summary.useSummaryStateQuery(id);
  const {data: models = []} = api.catalogs.useModelsQuery();
  const updateMutation = api.summary.useUpdateSummaryPromptSettingsMutation();
  const resetPromptMutation = api.summary.useResetSummaryPromptMutation();
  const resetSettingsMutation = api.summary.useResetSummaryPromptSettingsMutation();

  const advanced = useIsOpen(false);
  const resetPromptConfirm = useIsOpen(false);
  const resetSettingsConfirm = useIsOpen(false);
  const isInit = useRef(false);

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const {register, control, handleSubmit, reset, formState} = useForm<IPromptForm>({
    defaultValues: DEFAULTS
  });

  useEffect(() => {
    if (settings && !isInit.current) {
      isInit.current = true;
      reset({
        prompt: settings.prompt,
        model_name: settings.model_name,
        wiring_prefix: settings.wiring_prefix,
        wiring_suffix: settings.wiring_suffix,
        update_every_n_turns: settings.update_every_n_turns,
        char_limit: settings.char_limit,
        max_attempts: settings.max_attempts
      });
    }
  }, [settings, reset]);

  const isReplaying = state?.status === ESummaryStatus.Replaying;

  const onSubmit = (data: IPromptForm) => {
    updateMutation.mutate({
      id,
      data: {
        prompt: data.prompt,
        model_name: data.model_name,
        wiring_prefix: data.wiring_prefix,
        wiring_suffix: data.wiring_suffix,
        update_every_n_turns: data.update_every_n_turns,
        char_limit: data.char_limit,
        max_attempts: data.max_attempts
      }
    });
  };

  const handleConfirmResetPrompt = () => {
    resetPromptMutation.mutate(id, {
      onSuccess: (data) => {
        reset(data);
        resetPromptConfirm.close();
      }
    });
  };

  const handleConfirmResetSettings = () => {
    resetSettingsMutation.mutate(id, {
      onSuccess: (data) => {
        reset(data);
        resetSettingsConfirm.close();
      }
    });
  };

  if (isLoading) {
    return (
      <Box color={'{colors.textTertiary}'} p={'4px 0'}>
        {t('summary.loading')}
      </Box>
    );
  }

  return (
    <Stack h={'100%'} gap={'0'}>
      <Box flex={'1 1 auto'} overflowY={'auto'} p={'4px 0 16px'}>
        <Stack gap={'16px'}>
          <Box>
            <Flex justifyContent={'space-between'} alignItems={'center'}>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('summary.fields.updatePrompt')}
              </Box>
              {!isReplaying && (
                <Link onClick={resetPromptConfirm.open}>{t('summary.actions.resetToDefault')}</Link>
              )}
            </Flex>

            <Controller
              name={'prompt'}
              control={control}
              render={({field}) => (
                <Textarea
                  rows={14}
                  size={'lg'}
                  value={field.value}
                  autoresize={false}
                  disabled={isReplaying}
                  onChange={field.onChange}
                />
              )}
            />
          </Box>

          <Box>
            <Flex alignItems={'center'} justifyContent={'space-between'} mb={'8px'}>
              <Button
                pl={'10px'}
                size={'lg'}
                variant={'ghost'}
                color={'{colors.textSecondary}'}
                onClick={advanced.toggle}
              >
                <Flex alignItems={'center'} gap={'8px'}>
                  <Box>
                    <Box transform={advanced.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}>
                      <Svg name={'ArrowDown'} size={'s20'} />
                    </Box>
                  </Box>
                  <Box>{t('summary.actions.advancedSettings')}</Box>
                </Flex>
              </Button>
              {advanced.isOpen && !isReplaying && (
                <Link onClick={resetSettingsConfirm.open}>
                  {t('summary.actions.resetToDefaults')}
                </Link>
              )}
            </Flex>

            {advanced.isOpen && (
              <Stack pt={'12px'} gap={'12px'}>
                <Controller
                  control={control}
                  name={`model_name`}
                  render={({field: {onChange, value}}) => (
                    <Box>
                      <Box mb={'6px'}>{t('fields.model')}</Box>
                      <Select
                        value={value}
                        onChange={onChange}
                        disabled={isReplaying}
                        container={containerRef}
                        options={models.map((m) => ({value: m, label: m}))}
                      />
                    </Box>
                  )}
                />

                <Box>
                  <Box color={'{colors.textSecondary}'} mb={'6px'}>
                    {t('summary.fields.wiringPrefix')}
                  </Box>
                  <Input size={'lg'} disabled={isReplaying} {...register('wiring_prefix')} />
                </Box>

                <Box>
                  <Box color={'{colors.textSecondary}'} mb={'6px'}>
                    {t('summary.fields.wiringSuffix')}
                  </Box>
                  <Input size={'lg'} disabled={isReplaying} {...register('wiring_suffix')} />
                </Box>

                <Flex gap={'12px'} flexDirection={isLessThan768 ? 'column' : 'row'}>
                  <Box flex={1}>
                    <Box color={'{colors.textSecondary}'} mb={'6px'}>
                      {t('summary.fields.updateEveryNTurns')}
                    </Box>
                    <Input
                      size={'lg'}
                      type={'number'}
                      min={1}
                      disabled={isReplaying}
                      {...register('update_every_n_turns', {valueAsNumber: true})}
                    />
                  </Box>
                  <Box flex={1}>
                    <Box color={'{colors.textSecondary}'} mb={'6px'}>
                      {t('summary.fields.charLimit')}
                    </Box>
                    <Input
                      size={'lg'}
                      type={'number'}
                      min={1}
                      disabled={isReplaying}
                      {...register('char_limit', {valueAsNumber: true})}
                    />
                  </Box>
                  <Box flex={1}>
                    <Box color={'{colors.textSecondary}'} mb={'6px'}>
                      {t('summary.fields.maxAttempts')}
                    </Box>
                    <Input
                      size={'lg'}
                      type={'number'}
                      min={0}
                      disabled={isReplaying}
                      {...register('max_attempts', {valueAsNumber: true})}
                    />
                  </Box>
                </Flex>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>

      <Flex
        pt={'24px'}
        justifyContent={'flex-end'}
        borderTop={'thin solid'}
        borderColor={'{colors.bgMain}'}
      >
        <Button
          size={isLessThan768 ? 'md' : 'lg'}
          variant={'solid'}
          disabled={isReplaying || !formState.isValid || updateMutation.isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {t('summary.actions.saveSettings')}
        </Button>
      </Flex>

      <ConfirmDialog
        isOpen={resetPromptConfirm.isOpen}
        title={t('summary.confirm.resetPromptTitle')}
        body={t('summary.confirm.resetPromptBody')}
        isLoading={resetPromptMutation.isPending}
        onConfirm={handleConfirmResetPrompt}
        onCancel={resetPromptConfirm.close}
      />

      <ConfirmDialog
        isOpen={resetSettingsConfirm.isOpen}
        title={t('summary.confirm.resetSettingsTitle')}
        body={t('summary.confirm.resetSettingsBody')}
        isLoading={resetSettingsMutation.isPending}
        onConfirm={handleConfirmResetSettings}
        onCancel={resetSettingsConfirm.close}
      />
    </Stack>
  );
};

export const SummaryPromptTab = SummaryPromptTabFC;
